// import PropTypes from "prop-types";
// import { useFormContext, Controller } from "react-hook-form";
// import {
//   FormControl,
//   FormHelperText,
//   TextField,
//   Autocomplete,
// } from "@mui/material";

// RHFSearchableSelectField.propTypes = {
//   name: PropTypes.string.isRequired,
//   label: PropTypes.string,
//   options: PropTypes.array.isRequired,
//   variant: PropTypes.string,
//   disableOptions: PropTypes.array,
//   size: PropTypes.string,
//   width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
//   maxHeight: PropTypes.number,
//   enableNoneSelect: PropTypes.bool,
// };

// function RHFSearchableSelectField({
//   name,
//   options = [],
//   variant,
//   label,
//   disableOptions = [],
//   size = 'medium',
//   width = 150,
//   // maxHeight = 300,
//   enableNoneSelect = false,
//   noOptionsText = 'No options',
//   mt = 1,
//   mb = 1,
//   ...other
// }) {
//   // console.log(options, "options")
//   // console.log(noOptionsText, "noOptionsText")
//   const { control } = useFormContext();

//   return (
//     <Controller
//       name={name}
//       control={control}
//       render={({ field: { onChange, value }, fieldState: { error } }) => (
//         <FormControl error={!!error} sx={{ minWidth: width, mt, mb }}>
//           <Autocomplete
//             value={options.find(option => option.value === value) || null}
//             onChange={(event, item) => {
//               onChange(item ? item.value : '');
//             }}
//             options={options}
//             getOptionDisabled={(option) =>
//               disableOptions.includes(option.value)
//             }
//             componentsProps={{
//               paper: {
//                 sx: {
//                   width: 400,
//                   // height: 400,
//                   overflowY: 'auto',
//                   zIndex: 1300,
//                 },
//               },
//             }}

//             getOptionLabel={(option) => option.label}
//             renderOption={(props, option) => (
//               <li {...props} style={{ color: 'black' }}>
//                 {option.label}
//               </li>
//             )}
//             renderInput={(params) => (
//               <TextField
//                 {...params}
//                 label={label}
//                 variant={variant || "outlined"}
//                 error={!!error}
//                 helperText={error?.message}
//                 size={size}
//                 {...other}
//               />
//             )}
//             noOptionsText={options.length === 0 ? noOptionsText : noOptionsText}
//             fullWidth
//             size={size}
//           />
//           {enableNoneSelect && <FormHelperText sx={{ fontSize: 12 }}>Select "None" for no selection.</FormHelperText>}
//         </FormControl>
//       )}
//     />
//   );
// }

// export default RHFSearchableSelectField;

import PropTypes from 'prop-types';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';

RHFSearchableSelectField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  options: PropTypes.array, // [{ value, label }, ...]
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  noOptionsText: PropTypes.string,
  loading: PropTypes.bool,
  loadingText: PropTypes.node,
  debug: PropTypes.bool, // optional dev helper
};

export default function RHFSearchableSelectField({
  name,
  label,
  options = [],
  // width = 150,
  customWidth = 300, // default width
  fullWidth = false, // if true → takes full container width

  noOptionsText = 'No options',
  // While the options are still being fetched, show a spinner and "Loading…"
  // instead of "No options" — an empty list and a pending list look identical
  // otherwise, which reads as "this merchant has nothing" rather than "wait".
  loading = false,
  loadingText = 'Loading…',
  debug = false,
  placeholder,
  ...other
}) {
  const { control } = useFormContext();
  const [inputValue, setInputValue] = React.useState('');

  const filteredOptions = React.useMemo(() => {
    const terms = String(inputValue || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return options;
    return options.filter(opt => {
      // `secondary` (optional) is a muted second line under the label — e.g. a courier's logical
      // name. It is searchable too, so typing "FWD_SURFACE" finds every code under that name.
      const label = `${String(opt?.label ?? opt?.value ?? '')} ${String(opt?.secondary ?? '')}`.toLowerCase();
      return terms.every(t => label.includes(t));
    });
  }, [options, inputValue]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const selectedOption = options.find(opt => String(opt.value) === String(field.value)) || null;

        return (
          <Autocomplete
            {...other}
            options={filteredOptions}
            value={selectedOption}
            inputValue={inputValue}
            onInputChange={(_, newInput) => setInputValue(newInput ?? '')}
            onChange={(_, newVal) => field.onChange(newVal ? newVal.value : '')}
            filterOptions={(opts) => opts}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : String(opt?.label ?? ''))}
            isOptionEqualToValue={(opt, val) => String(opt?.value ?? opt) === String(val?.value ?? val)}
            loading={loading}
            loadingText={loadingText}
            noOptionsText={noOptionsText}
            componentsProps={{
              paper: {
                sx: {
                  // width: 300,
                  width: fullWidth ? '100%' : customWidth,
                  overflowY: 'auto',
                },
              },
            }}
            ListboxProps={{
              sx: {
                maxHeight: 450,
                overflowY: "auto",
                padding: 0,
                // boxShadow: '0px 6px 16px rgba(0,0,0,0.2)', // stronger shadow
                // borderRadius: '8px'          
              },
            }}

            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                placeholder={placeholder}
                error={!!error}
                helperText={error?.message}
                required={other.required}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} /> : null}
                      {params.InputProps?.endAdornment}
                    </>
                  ),
                }}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'off', // avoid browser autofill interfering
                  placeholder: placeholder ?? params.inputProps?.placeholder,
                }}
                // When a placeholder is supplied, force the floating label into
                // its "shrunk" (top-border) position so MUI actually renders the
                // placeholder inside the input. Without this MUI keeps the label
                // in the placeholder slot and hides the placeholder until focus —
                // which on FilterBar pages (where the floating label is CSS-hidden)
                // means the field looks completely empty.
                InputLabelProps={
                  placeholder
                    ? { shrink: true, ...other.InputLabelProps }
                    : other.InputLabelProps
                }
              />
            )}
            renderOption={(props, option) => (
              <li
                {...props}
                style={{
                  color: 'black',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '13px',
                  borderBottom: '1px solid #e8e8e8',
                  padding: '8px 12px',
                }}
                key={`${String(option.value)}-${String(option.label).slice(0, 30)}`}
              >
                {option.secondary ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <span>{option.label}</span>
                    <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', letterSpacing: 0.2 }}>
                      {option.secondary}
                    </span>
                  </div>
                ) : option.label}
              </li>
            )}
          />
        );
      }}
    />
  );
}
